import { useMemo } from 'react'
import DOMPurify, { type Config } from 'dompurify'
import { cn } from '@/lib/utils'

interface SanitizedHtmlProps {
  html: string
  className?: string
}

const ALLOWED_TAGS = [
  'p', 'div', 'span', 'br', 'hr',
  'ul', 'ol', 'li',
  'strong', 'b', 'em', 'i', 'u', 's', 'sub', 'sup',
  'a',
  'table', 'thead', 'tbody', 'tr', 'td', 'th',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'pre', 'code', 'blockquote',
]

// An approval card must show exactly what will be written to Azure DevOps, so the allow-list is
// explicit rather than DOMPurify's default profile: that profile keeps `hidden`, `inert`, `popover`,
// form controls and `style`/`color`/`bgcolor`, any of which hides or restyles content the approver
// is accepting. `img` is left out so expanding a card cannot fire a request to an arbitrary URL.
const SANITIZE_CONFIG: Config = {
  ALLOWED_TAGS,
  ALLOWED_ATTR: ['href', 'title', 'colspan', 'rowspan'],
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
}

const ELEMENT_STYLES = [
  '[&_p]:leading-relaxed [&_p]:mb-2 [&_p:last-child]:mb-0',
  '[&_ul]:list-disc [&_ul]:list-outside [&_ul]:ml-5 [&_ul]:mb-2 [&_ul]:space-y-0.5',
  '[&_ol]:list-decimal [&_ol]:list-outside [&_ol]:ml-5 [&_ol]:mb-2 [&_ol]:space-y-0.5',
  '[&_li]:leading-relaxed',
  '[&_strong]:font-semibold [&_b]:font-semibold [&_em]:italic [&_i]:italic',
  '[&_a]:text-info-foreground [&_a]:underline [&_a]:break-all',
  '[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono',
  '[&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-muted [&_pre]:p-2',
  '[&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-2',
  '[&_table]:w-full [&_table]:border [&_table]:border-border',
  '[&_th]:border [&_th]:border-border [&_th]:px-1.5 [&_th]:py-0.5 [&_th]:text-left [&_th]:font-semibold',
  '[&_td]:border [&_td]:border-border [&_td]:px-1.5 [&_td]:py-0.5',
  '[&>*:last-child]:mb-0',
].join(' ')

// Applied after sanitizing, so DOMPurify still drops `javascript:` hrefs before a target is added.
function openLinksInNewTab(sanitized: string): string {
  const doc = new DOMParser().parseFromString(sanitized, 'text/html')

  doc.querySelectorAll('a[href]').forEach((anchor) => {
    anchor.setAttribute('target', '_blank')
    anchor.setAttribute('rel', 'noopener noreferrer')
  })

  return doc.body.innerHTML
}

export function SanitizedHtml({ html, className }: SanitizedHtmlProps) {
  const sanitized = useMemo(() => openLinksInNewTab(DOMPurify.sanitize(html, SANITIZE_CONFIG)), [html])

  return (
    <div
      className={cn('break-words', ELEMENT_STYLES, className)}
      dangerouslySetInnerHTML={{ __html: sanitized }}
    />
  )
}
